import { useState, useEffect, useCallback, useRef, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from '@tanstack/react-router'
import { Plus, ChevronRight } from 'lucide-react'
import { Link } from '@tanstack/react-router'
import { useWalletStore } from '@/stores/useWalletStore'
import { usePairsStore } from '@/stores/usePairsStore'
import { useBreakpoint } from '@/hooks/useBreakpoint'
import { CommonFooter } from '@/components/layout'
import { useEosTransaction } from '@/hooks/useEosTransaction'
import { TokenRow } from '@/components/token/TokenRow'
import { TokenSelector } from '@/components/token/TokenSelector'
import { MyLiquidity, type LiquidityItem } from './MyLiquidity'
import { getPair } from '@/lib/swap-lib'
import { toFixed } from '@/lib/decimal'
import { parseQuantity, sleep } from '@/lib/common'
import { eosTool } from '@/services/blockchain/eos-tool'
import type { TokenInfo, Pair } from '@/types'

import poolHeader from '@/assets/images/pool-header.png'

interface PairState {
  pay: TokenInfo | null
  receive: TokenInfo | null
  pairId: number | null
  currentPair: Pair | null
}

export function PoolPage() {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const { isLogin, session } = useWalletStore()
  const { pairs, coins, fetchPairs } = usePairsStore()
  const { isMobile } = useBreakpoint()
  const { transact, loading: txLoading } = useEosTransaction()

  const [pair, setPair] = useState<PairState>({ pay: null, receive: null, pairId: null, currentPair: null })
  const [amount0, setAmount0] = useState('')
  const [amount1, setAmount1] = useState('')
  const [balance0, setBalance0] = useState<string | null>(null)
  const [balance1, setBalance1] = useState<string | null>(null)
  const [selectorType, setSelectorType] = useState<'pay' | 'receive' | null>(null)
  const [liquidityList, setLiquidityList] = useState<LiquidityItem[]>([])

  const balanceTimerRef = useRef<ReturnType<typeof setInterval> | null>(null)

  // Swap price for the current pair
  const swapPrice = useMemo(() => {
    if (!pair.currentPair) return 0
    const r0 = parseFloat(parseQuantity(pair.currentPair.token0.reserve).amount)
    const r1 = parseFloat(parseQuantity(pair.currentPair.token1.reserve).amount)
    if (r0 === 0) return 0
    return r1 / r0
  }, [pair.currentPair])

  // Initialize with EOS/USDT
  useEffect(() => {
    if (pairs.length === 0 || pair.pay) return
    const pay = coins.find((c) => c.tokenId === 'eosio.token-EOS') || coins[0]
    const receive = coins.find((c) => c.tokenId === 'tethertether-USDT') || coins[1]
    if (pay && receive) {
      const foundPair = getPair(pay, receive, pairs)
      setPair({ pay, receive, pairId: foundPair?.id ?? null, currentPair: foundPair ?? null })
    }
  }, [pairs, coins])

  // Fetch balances
  const fetchBalances = useCallback(async () => {
    if (!pair.pay || !pair.receive) {
      setBalance0(null)
      setBalance1(null)
      return
    }
    if (!isLogin || !session) {
      setBalance0(toFixed(0, pair.pay.precision))
      setBalance1(toFixed(0, pair.receive.precision))
      return
    }
    const account = session.account.name
    try {
      const [b0, b1] = await Promise.all([
        eosTool.getCurrencyBalance(pair.pay.contract, account, pair.pay.coin),
        eosTool.getCurrencyBalance(pair.receive.contract, account, pair.receive.coin),
      ])
      setBalance0(b0 ? b0.split(' ')[0] : '0')
      setBalance1(b1 ? b1.split(' ')[0] : '0')
    } catch { /* silently fail */ }
  }, [isLogin, session, pair.pay, pair.receive])

  useEffect(() => {
    fetchBalances()
    if (balanceTimerRef.current) clearInterval(balanceTimerRef.current)
    balanceTimerRef.current = setInterval(fetchBalances, 6000)
    return () => { if (balanceTimerRef.current) clearInterval(balanceTimerRef.current) }
  }, [fetchBalances])

  // Fetch user liquidity positions
  const fetchUserLiquidity = useCallback(async () => {
    if (!isLogin || !session || pairs.length === 0) {
      setLiquidityList([])
      return
    }
    const account = session.account.name
    try {
      const rows = await eosTool.getTableRows<any>({
        code: 'lptoken.ttr',
        scope: account,
        table: 'accounts',
        limit: 100,
        json: true,
      })
      if (!rows?.length) { setLiquidityList([]); return }

      const items: LiquidityItem[] = []
      for (const row of rows) {
        const balanceParts = row.balance.split(' ')
        const userToken = parseFloat(balanceParts[0])
        if (userToken <= 0) continue

        const pairId = row.pair_id
        const pairData = pairs.find((p) => p.id === pairId)
        if (!pairData) continue

        const totalToken = parseFloat(pairData.totalLiquidity)
        if (totalToken <= 0) continue

        const ratio = toFixed((userToken / totalToken) * 100, 4)
        const reserve0 = parseFloat(parseQuantity(pairData.token0.reserve).amount)
        const reserve1 = parseFloat(parseQuantity(pairData.token1.reserve).amount)
        const token0Quantity = toFixed(reserve0 * (userToken / totalToken), pairData.token0.precision)
        const token1Quantity = toFixed(reserve1 * (userToken / totalToken), pairData.token1.precision)

        const cost0 = row.cost0 !== undefined
          ? toFixed(row.cost0 / Math.pow(10, pairData.token0.precision), pairData.token0.precision)
          : token0Quantity
        const cost1 = row.cost1 !== undefined
          ? toFixed(row.cost1 / Math.pow(10, pairData.token1.precision), pairData.token1.precision)
          : token1Quantity

        items.push({
          pair: pairData,
          token0: pairData.token0,
          token1: pairData.token1,
          userToken,
          totalToken,
          ratio,
          token0Quantity,
          token1Quantity,
          cost0,
          cost1,
        })
      }
      setLiquidityList(items)
    } catch { setLiquidityList([]) }
  }, [isLogin, session, pairs])

  useEffect(() => {
    fetchUserLiquidity()
  }, [fetchUserLiquidity])

  // When pair changes, update currentPair
  useEffect(() => {
    if (pair.pay && pair.receive && pairs.length > 0) {
      const foundPair = getPair(pair.pay, pair.receive, pairs)
      setPair((prev) => ({ ...prev, pairId: foundPair?.id ?? null, currentPair: foundPair ?? null }))
    }
  }, [pair.pay, pair.receive, pairs])

  const handleAmount0Input = (value: string) => {
    setAmount0(value)
    if (!value || !pair.currentPair) { setAmount1(''); return }
    const num = parseFloat(value)
    if (isNaN(num)) return
    // Calculate token1 amount based on pool ratio
    const r0 = parseFloat(parseQuantity(pair.currentPair.token0.reserve).amount)
    const r1 = parseFloat(parseQuantity(pair.currentPair.token1.reserve).amount)
    if (r0 > 0) {
      // Determine which direction
      const isToken0Pay = pair.pay?.tokenId === pair.currentPair.token0.tokenId
      if (isToken0Pay) {
        setAmount1(toFixed(num * (r1 / r0), pair.receive?.precision ?? 4))
      } else {
        setAmount1(toFixed(num * (r0 / r1), pair.receive?.precision ?? 4))
      }
    }
  }

  const handleAmount1Input = (value: string) => {
    setAmount1(value)
    if (!value || !pair.currentPair) { setAmount0(''); return }
    const num = parseFloat(value)
    if (isNaN(num)) return
    const r0 = parseFloat(parseQuantity(pair.currentPair.token0.reserve).amount)
    const r1 = parseFloat(parseQuantity(pair.currentPair.token1.reserve).amount)
    if (r1 > 0) {
      const isToken0Pay = pair.pay?.tokenId === pair.currentPair.token0.tokenId
      if (isToken0Pay) {
        setAmount0(toFixed(num * (r0 / r1), pair.pay?.precision ?? 4))
      } else {
        setAmount0(toFixed(num * (r1 / r0), pair.pay?.precision ?? 4))
      }
    }
  }

  const handleTokenSelect = (token: TokenInfo) => {
    setPair((prev) => {
      const newPair = { ...prev }
      if (selectorType === 'pay') {
        if (prev.receive?.tokenId === token.tokenId) {
          newPair.pay = prev.receive
          newPair.receive = prev.pay
        } else {
          newPair.pay = token
        }
      } else {
        if (prev.pay?.tokenId === token.tokenId) {
          newPair.pay = prev.receive
          newPair.receive = prev.pay
        } else {
          newPair.receive = token
        }
      }
      return newPair
    })
    setAmount0('')
    setAmount1('')
  }

  const handleSubmit = async () => {
    if (!pair.pay || !pair.receive || !session || !pair.currentPair || !amount0 || !amount1) return

    const account = session.account
    const pairData = pair.currentPair
    const isToken0Pay = pair.pay.tokenId === pairData.token0.tokenId
    const token0 = isToken0Pay ? pair.pay : pair.receive
    const token1 = isToken0Pay ? pair.receive : pair.pay
    const qty0 = isToken0Pay ? amount0 : amount1
    const qty1 = isToken0Pay ? amount1 : amount0

    const actions = [
      {
        account: token0.contract,
        name: 'transfer',
        authorization: [{ actor: account.name, permission: account.authority || 'active' }],
        data: {
          from: account.name,
          to: 'swap.ttr',
          quantity: `${toFixed(parseFloat(qty0), token0.precision)} ${token0.coin}`,
          memo: `deposit:${pairData.id}`,
        },
      },
      {
        account: token1.contract,
        name: 'transfer',
        authorization: [{ actor: account.name, permission: account.authority || 'active' }],
        data: {
          from: account.name,
          to: 'swap.ttr',
          quantity: `${toFixed(parseFloat(qty1), token1.precision)} ${token1.coin}`,
          memo: `deposit:${pairData.id}`,
        },
      },
      {
        account: 'swap.ttr',
        name: 'addliquidity',
        authorization: [{ actor: account.name, permission: account.authority || 'active' }],
        data: {
          owner: account.name,
          pair_id: pairData.id,
        },
      },
    ]

    const result = await transact(actions, t('swap.addLiquiditySuccess'))
    if (result) {
      setAmount0('')
      setAmount1('')
      await sleep(500)
      await fetchPairs()
      fetchBalances()
      fetchUserLiquidity()
    }
  }

  const handleAddLiquidity = (pairData: Pair) => {
    setPair({
      pay: pairData.token0,
      receive: pairData.token1,
      pairId: pairData.id,
      currentPair: pairData,
    })
    setAmount0('')
    setAmount1('')
    window.scrollTo({ top: 0, behavior: 'smooth' })
  }

  const handleRemoveLiquidity = (item: LiquidityItem) => {
    navigate({
      to: '/pool/reduce/$item',
      params: { item: encodeURIComponent(JSON.stringify(item)) },
    })
  }

  const isInsufficientBalance0 = amount0 && balance0 && parseFloat(amount0) > parseFloat(balance0)
  const isInsufficientBalance1 = amount1 && balance1 && parseFloat(amount1) > parseFloat(balance1)

  const buttonText = () => {
    if (!isLogin) return t('public.login')
    if (!pair.pay || !pair.receive) return t('swap.selectToken')
    if (!pair.currentPair) return t('swap.noPairFound')
    if (!amount0 || !amount1) return t('swap.enterAmount')
    if (isInsufficientBalance0 || isInsufficientBalance1) return t('swap.insufficientBalance')
    return t('swap.addLiquidity')
  }

  const isButtonDisabled = !isLogin || !pair.currentPair || !amount0 || !amount1 || !!isInsufficientBalance0 || !!isInsufficientBalance1 || txLoading

  return (
    <div className="w-full animate-trade-enter">
    <div className="w-full flex justify-center px-4 md:px-[32px] md:mt-[32px]">
      <div className="w-full max-w-[400px]">
        {/* Pool mascot */}
        {!isMobile && (
          <div className="flex justify-center">
            <img src={poolHeader} className="w-[129px]" alt="" />
          </div>
        )}

        {/* Pool Card */}
        <div className="bg-white rounded-[26px] shadow-[0_0_8px_1px_rgba(61,98,50,0.14)] px-[16px] py-[24px]">
          <TokenRow
            token={pair.pay}
            amount={amount0}
            balance={balance0}
            onTokenClick={() => setSelectorType('pay')}
            onInput={handleAmount0Input}
            onBalanceClick={() => { if (balance0) handleAmount0Input(balance0) }}
          />

          {/* Plus icon */}
          <div className="flex justify-center my-[18px]">
            <div className="w-[42px] h-[42px] rounded-full bg-[#498846] flex items-center justify-center">
              <Plus className="h-5 w-5 text-white" />
            </div>
          </div>

          <TokenRow
            token={pair.receive}
            amount={amount1}
            balance={balance1}
            onTokenClick={() => setSelectorType('receive')}
            onInput={handleAmount1Input}
            onBalanceClick={() => { if (balance1) handleAmount1Input(balance1) }}
          />

          {/* Pool info */}
          {pair.currentPair && (
            <div className="mt-[10px] py-[5px] text-[13px]">
              <div className="flex items-center justify-between leading-[20px]">
                <span className="text-[#666]">{t('swap.poolReserves')}</span>
                <span className="text-[#333]">
                  {pair.currentPair.token0.reserve} / {pair.currentPair.token1.reserve}
                </span>
              </div>
              <div className="flex items-center justify-between leading-[20px] mt-[3px]">
                <span className="text-[#666]">{t('public.price')}</span>
                <span className="text-[#333]">
                  1 {pair.currentPair.token0.coin} ≈ {toFixed(swapPrice, 8)} {pair.currentPair.token1.coin}
                </span>
              </div>
            </div>
          )}

          {/* Submit button */}
          <div className="mt-[21px]">
            <button
              onClick={handleSubmit}
              disabled={isButtonDisabled}
              className="w-full h-[48px] rounded-[24px] bg-[#498846] text-white text-[18px] font-semibold cursor-pointer disabled:opacity-60 disabled:cursor-not-allowed hover:opacity-95 transition-opacity flex items-center justify-center"
            >
              {txLoading ? t('public.loading') : buttonText()}
            </button>
          </div>

          {/* Create Market link */}
          <div className="flex justify-center items-center gap-1 mt-[16px]">
            <Link to="/pool/create-market" className="text-[14px] font-medium text-primary flex items-center gap-1">
              {t('swap.createMarket')}
              <ChevronRight className="h-4 w-4" />
            </Link>
          </div>
        </div>

        {/* My Liquidity Positions */}
        <MyLiquidity
          liquidityList={liquidityList}
          onAddLiquidity={handleAddLiquidity}
          onRemoveLiquidity={handleRemoveLiquidity}
        />
      </div>

      <TokenSelector
        open={selectorType !== null}
        onClose={() => setSelectorType(null)}
        onSelect={handleTokenSelect}
        coins={coins}
        selected={selectorType === 'pay' ? pair.pay : pair.receive}
      />
    </div>
    <CommonFooter />
    </div>
  )
}
